// SalesReport.js  –  Fresh Hills Admin  (FIXED)
// ✅ Uses fast single-query endpoints instead of per-order item fetching
// ✅ Day-wise sales breakdown tab
// ✅ Proper error handling and loading states
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import {
  CurrencyRupeeIcon, ShoppingBagIcon, ArrowTrendingUpIcon,
  CalendarIcon, ArrowDownTrayIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Title, Tooltip, Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend);

const API = process.env.REACT_APP_API_URL || 'https://freshhillsmilk.in/api';

const authHeaders = () => {
  const token = localStorage.getItem('adminToken') ||
                localStorage.getItem('fh_admin_token') ||
                localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};

const fmt    = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtRs  = (n) => `₹${fmt(n)}`;
const fmtDay = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' });
};

// ─────────────────────────────────────────────────────────────────────────────
const SalesReport = () => {
  const [tab,          setTab]          = useState('overview');   // 'overview' | 'daywise'
  const [period,       setPeriod]       = useState('weekly');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [stats,        setStats]        = useState(null);
  const [trendData,    setTrendData]    = useState([]);
  const [productData,  setProductData]  = useState([]);
  const [channelData,  setChannelData]  = useState({ self_service: 0, delivery: 0 });
  const [daywiseData,  setDaywiseData]  = useState([]);
  const [daywiseDays,  setDaywiseDays]  = useState(30);
  const [allOrders,    setAllOrders]    = useState([]);

  // ── Fast product fetch — single SQL query ──────────────────────────────────
  const fetchProductData = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/admin/sales/products?limit=7`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success && data.products) {
        return data.products.map(p => ({
          name:    p.product_name,
          units:   p.units_sold,
          revenue: p.revenue,
        }));
      }
    } catch (e) {
      // fallback: no product data
    }
    return [];
  }, []);

  // ── Fast day-wise fetch ────────────────────────────────────────────────────
  const fetchDaywise = useCallback(async (days) => {
    try {
      const res  = await fetch(`${API}/admin/sales/daywise?days=${days}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) return data.data || [];
    } catch (e) {}
    return [];
  }, []);

  // ── Fetch orders for CSV + channel breakdown (limited to 200) ─────────────
  const fetchOrders = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/admin/orders?limit=200&page=1`, { headers: authHeaders() });
      const data = await res.json();
      return data.orders || [];
    } catch { return []; }
  }, []);

  // ── Build trend from day-wise data ────────────────────────────────────────
  const buildTrend = useCallback((daywise, p) => {
    const n   = p === 'weekly' ? 7 : 30;
    const cut = daywise.slice(-n);
    setTrendData(cut.map(d => ({
      label: fmtDay(d.day),
      value: d.revenue,
    })));
  }, []);

  // ── Main load ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, products, daywise, orders] = await Promise.all([
        fetch(`${API}/admin/orders/stats`,    { headers: authHeaders() }).then(r => r.json()),
        fetchProductData(),
        fetchDaywise(30),
        fetchOrders(),
      ]);

      if (statsRes.success) setStats(statsRes.stats);

      setProductData(products);
      setDaywiseData(daywise);
      setAllOrders(orders);

      // Channel breakdown from orders
      const ch = { self_service: 0, delivery: 0 };
      orders.forEach(o => {
        if (o.order_type === 'self_service') ch.self_service++;
        else ch.delivery++;
      });
      setChannelData(ch);

      buildTrend(daywise, period);

    } catch (e) {
      setError('Failed to load sales data. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchProductData, fetchDaywise, fetchOrders, buildTrend, period]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Rebuild trend when period changes
  useEffect(() => {
    if (daywiseData.length) buildTrend(daywiseData, period);
  }, [period, daywiseData, buildTrend]);

  // Reload day-wise when range changes
  useEffect(() => {
    if (tab !== 'daywise') return;
    fetchDaywise(daywiseDays).then(d => {
      setDaywiseData(d);
      buildTrend(d, period);
    });
  }, [daywiseDays]); // eslint-disable-line

  // ── Export CSV ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (tab === 'daywise' && daywiseData.length) {
      const header = ['Date','Orders','Revenue','QR Orders','Delivery Orders','Avg Order Value'];
      const rows   = daywiseData.map(d => [
        d.day, d.total_orders, d.revenue, d.qr_orders, d.delivery_orders, d.avg_order_value,
      ]);
      const csv  = [header, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const a    = document.createElement('a');
      a.href     = URL.createObjectURL(blob);
      a.download = `daywise_sales_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      return;
    }
    if (!allOrders.length) return;
    const header = ['Order #','Customer','Phone','Amount','Type','Status','Date'];
    const rows   = allOrders.map(o => [
      o.order_number, o.customer_name || 'Guest', o.customer_phone || '—',
      o.total_amount, o.order_type, o.status,
      o.created_at ? new Date(o.created_at).toLocaleString('en-IN') : '',
    ]);
    const csv  = [header, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(blob);
    a.download = `orders_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // ── Chart data ────────────────────────────────────────────────────────────
  const lineData = {
    labels: trendData.map(d => d.label),
    datasets: [{
      label: 'Revenue (₹)',
      data:  trendData.map(d => d.value),
      borderColor: '#059669', backgroundColor: 'rgba(5,150,105,0.08)',
      tension: 0.4, fill: true,
      pointBackgroundColor: '#059669', pointBorderColor: 'white',
      pointBorderWidth: 2, pointRadius: 5,
    }],
  };

  const barData = {
    labels: productData.map(p => p.name?.length > 14 ? p.name.slice(0,14)+'…' : p.name),
    datasets: [{
      label: 'Units Sold',
      data:  productData.map(p => p.units),
      backgroundColor: ['#059669','#0891b2','#7c3aed','#f59e0b','#10b981','#06b6d4','#8b5cf6'],
      borderRadius: 8,
    }],
  };

  const daywiseBarData = {
    labels: daywiseData.slice(-14).map(d => fmtDay(d.day)),
    datasets: [{
      label: 'Revenue (₹)',
      data:  daywiseData.slice(-14).map(d => d.revenue),
      backgroundColor: daywiseData.slice(-14).map((_, i) =>
        i === daywiseData.slice(-14).length - 1 ? '#059669' : 'rgba(5,150,105,0.55)'
      ),
      borderRadius: 6,
    }],
  };

  const totalCh   = channelData.self_service + channelData.delivery;
  const donutData = {
    labels: ['Self Service (QR)', 'Online Delivery'],
    datasets: [{
      data: [channelData.self_service, channelData.delivery],
      backgroundColor: ['#059669','#0891b2'],
      borderWidth: 0,
    }],
  };

  const chartOpts = (yPrefix = '₹') => ({
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        grid: { color: '#f1f5f9' },
        ticks: { color:'#64748b', font:{ family:'Nunito', weight:'700', size:11 },
          callback: v => `${yPrefix}${v.toLocaleString('en-IN')}` },
      },
      x: { grid:{ display:false }, ticks:{ color:'#64748b', font:{ family:'Nunito', weight:'700', size:10 } } },
    },
  });

  const totalOrders   = stats?.total_orders   || 0;
  const totalRevenue  = stats?.total_revenue  || 0;
  const todayRevenue  = stats?.today_revenue  || 0;
  const todayOrders   = stats?.today_orders   || 0;
  const avgOrderValue = totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(0) : 0;
  const maxProd       = productData.length ? Math.max(...productData.map(p => p.revenue)) : 1;

  const statCards = [
    { label:"Today's Revenue", value:fmtRs(todayRevenue),  sub:`${todayOrders} orders today`,         color:'#059669', border:'#a7f3d0' },
    { label:'Total Orders',    value:fmt(totalOrders),      sub:`${stats?.completed||0} completed`,    color:'#2563eb', border:'#bfdbfe' },
    { label:'Avg Order Value', value:fmtRs(avgOrderValue),  sub:'Per transaction',                     color:'#7c3aed', border:'#ddd6fe' },
    { label:'Total Revenue',   value:fmtRs(totalRevenue),   sub:'All time',                            color:'#d97706', border:'#fde68a' },
  ];

  // Day-wise totals
  const dwTotal   = daywiseData.reduce((a, d) => a + d.total_orders, 0);
  const dwRevenue = daywiseData.reduce((a, d) => a + d.revenue,      0);
  const dwBestDay = daywiseData.reduce((a, d) => (!a || d.revenue > a.revenue ? d : a), null);

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; }

    .sr-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
    .sr-title { font-size:1.55rem; font-weight:900; color:#111827; }
    .sr-sub   { font-size:0.85rem; color:#64748b; font-weight:600; margin-top:0.15rem; }

    .sr-ctrl { display:flex; align-items:center; gap:0.65rem; flex-wrap:wrap; }
    .sr-pg   { display:flex; background:#f1f5f9; border-radius:9999px; padding:0.22rem; }
    .sr-pb   { padding:0.38rem .9rem; border-radius:9999px; border:none; cursor:pointer; font-weight:700; font-size:0.8rem; color:#64748b; background:none; font-family:'Nunito',sans-serif; transition:all 0.2s; }
    .sr-pb.act { background:white; color:#059669; box-shadow:0 2px 8px rgba(0,0,0,.08); }

    .sr-tabs { display:flex; gap:0.5rem; margin-bottom:1.5rem; background:#f1f5f9; border-radius:9999px; padding:0.25rem; width:fit-content; }
    .sr-tab  { padding:0.5rem 1.35rem; border-radius:9999px; border:none; cursor:pointer; font-weight:700; font-size:0.85rem; color:#64748b; background:none; font-family:'Nunito',sans-serif; transition:all 0.2s; }
    .sr-tab.act { background:white; color:#059669; box-shadow:0 2px 8px rgba(0,0,0,.09); }

    .sr-exp { display:flex; align-items:center; gap:0.38rem; padding:0.42rem .95rem; border-radius:9999px; background:#ecfdf5; color:#059669; border:1.5px solid #a7f3d0; font-weight:700; font-size:0.8rem; cursor:pointer; transition:all 0.2s; font-family:'Nunito',sans-serif; }
    .sr-exp svg { width:0.85rem; height:0.85rem; }
    .sr-exp:hover { background:#059669; color:white; border-color:#059669; }

    .sr-ref { display:flex; align-items:center; gap:0.35rem; padding:0.42rem .95rem; border-radius:9999px; background:white; color:#374151; border:1.5px solid #e5e7eb; font-weight:700; font-size:0.8rem; cursor:pointer; transition:all 0.2s; font-family:'Nunito',sans-serif; }
    .sr-ref svg { width:0.82rem; height:0.82rem; }
    .sr-ref:hover { border-color:#a7f3d0; color:#059669; }

    .sr-stats { display:grid; grid-template-columns:1fr; gap:1.1rem; margin-bottom:1.75rem; }
    @media(min-width:560px){ .sr-stats{ grid-template-columns:repeat(2,1fr); } }
    @media(min-width:1024px){ .sr-stats{ grid-template-columns:repeat(4,1fr); } }

    .sr-sc { background:white; border-radius:1.25rem; padding:1.25rem 1.35rem; border:1.5px solid; box-shadow:0 3px 10px rgba(0,0,0,.04); transition:all .3s; }
    .sr-sc:hover { transform:translateY(-3px); box-shadow:0 10px 20px rgba(0,0,0,.07); }
    .sr-sc-lbl { font-size:0.75rem; font-weight:700; color:#64748b; margin-bottom:0.35rem; text-transform:uppercase; letter-spacing:.3px; }
    .sr-sc-val { font-size:1.65rem; font-weight:900; color:#111827; margin-bottom:0.2rem; }
    .sr-sc-sub { font-size:0.7rem; font-weight:600; }

    .sr-grid2 { display:grid; grid-template-columns:1fr; gap:1.35rem; margin-bottom:1.35rem; }
    @media(min-width:1024px){ .sr-grid2{ grid-template-columns:2fr 1fr; } }

    .sr-row2 { display:grid; grid-template-columns:1fr; gap:1.35rem; margin-bottom:1.35rem; }
    @media(min-width:768px){ .sr-row2{ grid-template-columns:1fr 1fr; } }

    .sr-card { background:white; border-radius:1.35rem; padding:1.35rem; border:1px solid #f1f5f9; box-shadow:0 3px 14px rgba(0,0,0,.04); }
    .sr-ct   { font-size:0.9rem; font-weight:800; color:#111827; margin-bottom:1.15rem; display:flex; align-items:center; gap:0.45rem; }
    .sr-ct svg { width:0.95rem; height:0.95rem; color:#059669; }

    .sr-pr { display:flex; align-items:center; gap:.85rem; padding:.65rem 0; border-bottom:1px solid #f8fafc; }
    .sr-pr:last-child { border-bottom:none; }
    .sr-pr-rk { width:26px; height:26px; border-radius:50%; background:#ecfdf5; color:#059669; font-weight:900; font-size:0.75rem; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .sr-pr-nm { font-weight:700; color:#111827; font-size:0.85rem; flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .sr-pr-bw { width:70px; height:5px; background:#f1f5f9; border-radius:9999px; overflow:hidden; flex-shrink:0; }
    .sr-pr-b  { height:100%; background:linear-gradient(90deg,#10b981,#0891b2); border-radius:9999px; }
    .sr-pr-u  { font-size:0.72rem; color:#64748b; font-weight:600; width:65px; text-align:right; flex-shrink:0; }
    .sr-pr-rv { font-weight:800; color:#059669; font-size:0.85rem; width:75px; text-align:right; flex-shrink:0; }

    /* Day-wise table */
    .dw-toolbar { display:flex; align-items:center; gap:0.75rem; margin-bottom:1.25rem; flex-wrap:wrap; }
    .dw-range   { display:flex; background:#f1f5f9; border-radius:9999px; padding:0.22rem; }
    .dw-rb      { padding:0.35rem .8rem; border-radius:9999px; border:none; cursor:pointer; font-weight:700; font-size:0.78rem; color:#64748b; background:none; font-family:'Nunito',sans-serif; transition:all 0.2s; }
    .dw-rb.act  { background:white; color:#059669; box-shadow:0 2px 6px rgba(0,0,0,.08); }

    .dw-sum { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-bottom:1.5rem; }
    .dw-sum-card { background:white; border-radius:1.1rem; padding:1rem 1.25rem; border:1.5px solid #e5e7eb; text-align:center; }
    .dw-sum-val { font-size:1.4rem; font-weight:900; }
    .dw-sum-lbl { font-size:0.72rem; font-weight:700; color:#64748b; margin-top:0.15rem; }

    .dw-table-wrap { background:white; border-radius:1.25rem; overflow:hidden; border:1px solid #f1f5f9; box-shadow:0 3px 14px rgba(0,0,0,.04); }
    .dw-table      { width:100%; border-collapse:collapse; }
    .dw-th { padding:0.85rem 1.1rem; text-align:left; font-size:0.72rem; font-weight:800; color:#64748b; text-transform:uppercase; letter-spacing:.5px; background:#f8fafc; border-bottom:1px solid #f1f5f9; white-space:nowrap; }
    .dw-td { padding:0.85rem 1.1rem; border-bottom:1px solid #f8fafc; font-size:0.875rem; color:#374151; vertical-align:middle; }
    .dw-tr:last-child .dw-td { border-bottom:none; }
    .dw-tr:hover .dw-td { background:#fafbfc; }
    .dw-tr.today .dw-td { background:#f0fdf4; }
    .dw-rev { font-weight:900; color:#059669; }
    .dw-orders { font-weight:700; color:#2563eb; }
    .dw-bar-wrap { width:80px; height:6px; background:#f1f5f9; border-radius:9999px; overflow:hidden; display:inline-block; vertical-align:middle; margin-left:0.5rem; }
    .dw-bar      { height:100%; background:linear-gradient(90deg,#10b981,#059669); border-radius:9999px; }

    .sr-empty { text-align:center; padding:2rem 1rem; color:#9ca3af; font-size:.85rem; font-weight:600; }
    .sr-empty-ico { font-size:2rem; margin-bottom:.5rem; }
    .sr-err { background:#fef2f2; border:1px solid #fecaca; border-radius:1rem; padding:1rem 1.25rem; color:#dc2626; font-weight:600; font-size:.85rem; margin-bottom:1rem; }

    @keyframes sk{0%,100%{opacity:1}50%{opacity:.35}}
    .sk { animation:sk 1.4s ease-in-out infinite; background:#f3f4f6; border-radius:.75rem; }
    .sk-card { height:110px; }
    .sk-chart { height:260px; }
    @keyframes spin{to{transform:rotate(360deg)}}
  `;

  return (
    <AdminLayout>
      <style>{css}</style>

      {/* Header */}
      <div className="sr-hdr">
        <div>
          <div className="sr-title">📊 Sales Report</div>
          <div className="sr-sub">Real-time revenue, orders and product performance</div>
        </div>
        <div className="sr-ctrl">
          {tab === 'overview' && (
            <div className="sr-pg">
              {['weekly','monthly'].map(p => (
                <button key={p} className={`sr-pb${period===p?' act':''}`} onClick={() => setPeriod(p)}>
                  {p.charAt(0).toUpperCase()+p.slice(1)}
                </button>
              ))}
            </div>
          )}
          <button className="sr-ref" onClick={load} disabled={loading}>
            <ArrowPathIcon style={loading?{animation:'spin 1s linear infinite'}:{}}/> Refresh
          </button>
          <button className="sr-exp" onClick={exportCSV}>
            <ArrowDownTrayIcon/> Export CSV
          </button>
        </div>
      </div>

      {error && <div className="sr-err">⚠️ {error}</div>}

      {/* Tabs */}
      <div className="sr-tabs">
        <button className={`sr-tab${tab==='overview'?' act':''}`} onClick={() => setTab('overview')}>
          📈 Overview
        </button>
        <button className={`sr-tab${tab==='daywise'?' act':''}`} onClick={() => setTab('daywise')}>
          📅 Day-wise
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* OVERVIEW TAB                                                        */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'overview' && (
        <>
          {/* Stat Cards */}
          <div className="sr-stats">
            {loading
              ? Array.from({length:4}).map((_,i) => <div key={i} className="sk sk-card"/>)
              : statCards.map(s => (
                <div key={s.label} className="sr-sc" style={{ borderColor:s.border }}>
                  <div className="sr-sc-lbl">{s.label}</div>
                  <div className="sr-sc-val">{s.value}</div>
                  <div className="sr-sc-sub" style={{ color:s.color }}>{s.sub}</div>
                </div>
              ))}
          </div>

          {/* Revenue Trend + Channel */}
          <div className="sr-grid2">
            <div className="sr-card">
              <div className="sr-ct"><ArrowTrendingUpIcon/> Revenue Trend ({period})</div>
              {loading
                ? <div className="sk sk-chart"/>
                : trendData.every(d => d.value === 0)
                  ? <div className="sr-empty"><div className="sr-empty-ico">📈</div>No revenue data yet</div>
                  : <Line data={lineData} options={chartOpts()}/>}
            </div>
            <div className="sr-card">
              <div className="sr-ct"><ShoppingBagIcon/> Orders by Channel</div>
              {loading
                ? <div className="sk sk-chart"/>
                : totalCh === 0
                  ? <div className="sr-empty"><div className="sr-empty-ico">🛒</div>No orders yet</div>
                  : <>
                      <Doughnut data={donutData} options={{
                        responsive:true,
                        plugins:{ legend:{ position:'bottom', labels:{ font:{ family:'Nunito', weight:'700' }, color:'#374151', padding:16 } } },
                      }}/>
                      <div style={{display:'flex',justifyContent:'center',gap:'1.5rem',marginTop:'.75rem',fontSize:'.78rem',fontWeight:'700',color:'#374151'}}>
                        <span>🟢 QR: {channelData.self_service}</span>
                        <span>🔵 Delivery: {channelData.delivery}</span>
                      </div>
                    </>}
            </div>
          </div>

          {/* Bar + Top Products */}
          <div className="sr-row2">
            <div className="sr-card">
              <div className="sr-ct"><ShoppingBagIcon/> Top Products — Units Sold</div>
              {loading
                ? <div className="sk sk-chart"/>
                : productData.length === 0
                  ? <div className="sr-empty"><div className="sr-empty-ico">📦</div>No product sales data yet</div>
                  : <Bar data={barData} options={chartOpts('')}/>}
            </div>
            <div className="sr-card">
              <div className="sr-ct"><CurrencyRupeeIcon/> Top Products — Revenue</div>
              {loading
                ? <div className="sk sk-chart"/>
                : productData.length === 0
                  ? <div className="sr-empty"><div className="sr-empty-ico">📦</div>No product sales data yet</div>
                  : productData.map((p,i) => (
                    <div key={p.name} className="sr-pr">
                      <div className="sr-pr-rk">{i+1}</div>
                      <div className="sr-pr-nm" title={p.name}>{p.name}</div>
                      <div className="sr-pr-bw">
                        <div className="sr-pr-b" style={{width:`${Math.round((p.revenue/maxProd)*100)}%`}}/>
                      </div>
                      <div className="sr-pr-u">{p.units} units</div>
                      <div className="sr-pr-rv">{fmtRs(p.revenue)}</div>
                    </div>
                  ))}
            </div>
          </div>

          {/* Order status summary */}
          {!loading && stats && (
            <div className="sr-card" style={{marginBottom:'1.35rem'}}>
              <div className="sr-ct"><CalendarIcon/> Order Status Summary</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:'.85rem'}}>
                {[
                  {label:'Pending',    value:stats.pending    ||0, color:'#d97706', bg:'#fef3c7'},
                  {label:'Confirmed',  value:stats.confirmed  ||0, color:'#2563eb', bg:'#eff6ff'},
                  {label:'Dispatched', value:stats.dispatched ||0, color:'#7c3aed', bg:'#faf5ff'},
                  {label:'Completed',  value:stats.completed  ||0, color:'#059669', bg:'#ecfdf5'},
                  {label:'Cancelled',  value:stats.cancelled  ||0, color:'#dc2626', bg:'#fef2f2'},
                ].map(s => (
                  <div key={s.label} style={{background:s.bg,borderRadius:'.85rem',padding:'.75rem 1rem',textAlign:'center'}}>
                    <div style={{fontSize:'1.5rem',fontWeight:'900',color:s.color}}>{s.value}</div>
                    <div style={{fontSize:'.72rem',fontWeight:'700',color:'#6b7280',marginTop:'.15rem'}}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* DAY-WISE TAB                                                        */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {tab === 'daywise' && (
        <>
          {/* Range selector */}
          <div className="dw-toolbar">
            <span style={{fontWeight:700,fontSize:'0.85rem',color:'#374151'}}>Show last:</span>
            <div className="dw-range">
              {[7,14,30,60,90].map(d => (
                <button key={d}
                  className={`dw-rb${daywiseDays===d?' act':''}`}
                  onClick={() => setDaywiseDays(d)}>
                  {d} days
                </button>
              ))}
            </div>
          </div>

          {/* Summary cards */}
          <div className="dw-sum">
            <div className="dw-sum-card" style={{borderColor:'#a7f3d0'}}>
              <div className="dw-sum-val" style={{color:'#059669'}}>{fmtRs(dwRevenue)}</div>
              <div className="dw-sum-lbl">Total Revenue</div>
            </div>
            <div className="dw-sum-card" style={{borderColor:'#bfdbfe'}}>
              <div className="dw-sum-val" style={{color:'#2563eb'}}>{dwTotal}</div>
              <div className="dw-sum-lbl">Total Orders</div>
            </div>
            <div className="dw-sum-card" style={{borderColor:'#fde68a'}}>
              <div className="dw-sum-val" style={{color:'#d97706'}}>
                {dwBestDay ? fmtDay(dwBestDay.day) : '—'}
              </div>
              <div className="dw-sum-lbl">Best Day {dwBestDay ? `(${fmtRs(dwBestDay.revenue)})` : ''}</div>
            </div>
          </div>

          {/* Bar chart */}
          <div className="sr-card" style={{marginBottom:'1.35rem'}}>
            <div className="sr-ct"><ArrowTrendingUpIcon/> Daily Revenue (last 14 days)</div>
            {loading
              ? <div className="sk sk-chart"/>
              : daywiseData.length === 0
                ? <div className="sr-empty"><div className="sr-empty-ico">📅</div>No day-wise data yet</div>
                : <Bar data={daywiseBarData} options={chartOpts()}/>}
          </div>

          {/* Table */}
          <div className="dw-table-wrap">
            {loading
              ? <div style={{padding:'2rem',textAlign:'center',color:'#94a3b8',fontWeight:700}}>Loading…</div>
              : daywiseData.length === 0
                ? <div className="sr-empty"><div className="sr-empty-ico">📅</div>No sales data found</div>
                : (
                  <table className="dw-table">
                    <thead>
                      <tr>
                        {['Date','Orders','Revenue','QR Orders','Delivery','Avg Order Value'].map(h => (
                          <th key={h} className="dw-th">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...daywiseData].reverse().map(d => {
                        const today = new Date().toISOString().slice(0,10);
                        const isToday = d.day === today;
                        const maxRev = Math.max(...daywiseData.map(x => x.revenue)) || 1;
                        return (
                          <tr key={d.day} className={`dw-tr${isToday?' today':''}`}>
                            <td className="dw-td">
                              <strong>{fmtDay(d.day)}</strong>
                              {isToday && <span style={{marginLeft:'0.5rem',fontSize:'0.7rem',background:'#ecfdf5',color:'#059669',padding:'0.1rem 0.45rem',borderRadius:'9999px',fontWeight:800}}>Today</span>}
                            </td>
                            <td className="dw-td">
                              <span className="dw-orders">{d.total_orders}</span>
                            </td>
                            <td className="dw-td">
                              <span className="dw-rev">{fmtRs(d.revenue)}</span>
                              <span className="dw-bar-wrap">
                                <span className="dw-bar" style={{width:`${Math.round((d.revenue/maxRev)*100)}%`}}/>
                              </span>
                            </td>
                            <td className="dw-td">{d.qr_orders || 0}</td>
                            <td className="dw-td">{d.delivery_orders || 0}</td>
                            <td className="dw-td">{fmtRs(d.avg_order_value)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default SalesReport;